import { useState } from "react";
import { CalcCard, NumField, ResultRow, PrimaryButton, Section, SaveButton } from "@/components/CalcUI";
import { runCalc } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

export default function Fishing() {
  const [yieldIn, setYieldIn] = useState({ od: "5.0", id: "4.276", yield_strength: "75000" });
  const [yieldOut, setYieldOut] = useState(null);

  const [pull, setPull] = useState({
    od: "5.0",
    id: "4.276",
    yield_strength: "75000",
    pull_factor: "0.9",
    pipe_weight_ppf: "19.5",
    length_ft: "10000",
    mw: "10.0",
  });
  const [pullOut, setPullOut] = useState(null);

  const [jar, setJar] = useState({
    jar_setting_lbf: "50000",
    length_above_jar_ft: "9500",
    pipe_weight_ppf: "19.5",
    od: "5.0",
    id: "4.276",
  });
  const [jarOut, setJarOut] = useState(null);

  const run = async (calc, inp, setter) => {
    try {
      const r = await runCalc(calc, Object.fromEntries(Object.entries(inp).map(([k, v]) => [k, parseFloat(v)])));
      setter(r);
      if (r.error) toast.error(r.error);
    } catch {
      toast.error("Invalid inputs");
    }
  };

  return (
    <div data-testid="fishing-page">
      <Section
        title="Fishing Calculations"
        subtitle="Stuck-pipe response: pipe body yield, safe pull limits with buoyed string weight, and jar impact energy. Use to green-light overpull attempts and jar strategy without paper tables."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <CalcCard testid="mys-card" title="Pipe Body Yield (MYS)" formula="MYS = A · Yp ; A = π/4·(OD²−ID²)">
            <div className="grid grid-cols-3 gap-3">
              <NumField testid="mys-od" label="OD" unit="in" value={yieldIn.od} onChange={(v) => setYieldIn({ ...yieldIn, od: v })} />
              <NumField testid="mys-id" label="ID" unit="in" value={yieldIn.id} onChange={(v) => setYieldIn({ ...yieldIn, id: v })} />
              <NumField testid="mys-yp" label="Yield" unit="psi" value={yieldIn.yield_strength} onChange={(v) => setYieldIn({ ...yieldIn, yield_strength: v })} />
            </div>
            <div className="mt-4 flex justify-between">
              <PrimaryButton testid="mys-run" onClick={() => run("pipe_body_yield", yieldIn, setYieldOut)}>Calculate</PrimaryButton>
              <SaveButton getPayload={() => yieldOut && { category: "Fishing", calculator: "pipe_body_yield", inputs: yieldIn, outputs: yieldOut, units: "field", default_name: "Pipe MYS" }} />
            </div>
            {yieldOut && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Cross-section A" value={yieldOut.area_in2} unit="in²" />
                <ResultRow label="Minimum Yield Strength" value={yieldOut.yield_strength_lbf?.toLocaleString()} unit="lbf" emphasis />
              </div>
            )}
          </CalcCard>

          <CalcCard testid="pull-card" title="Safe Pull Limit / Overpull" formula="Overpull = MYS·factor − W·L·(1−MW/65.5)">
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="pull-od" label="OD" unit="in" value={pull.od} onChange={(v) => setPull({ ...pull, od: v })} />
              <NumField testid="pull-id" label="ID" unit="in" value={pull.id} onChange={(v) => setPull({ ...pull, id: v })} />
              <NumField testid="pull-yp" label="Yield" unit="psi" value={pull.yield_strength} onChange={(v) => setPull({ ...pull, yield_strength: v })} />
              <NumField testid="pull-factor" label="Pull factor" unit="—" value={pull.pull_factor} onChange={(v) => setPull({ ...pull, pull_factor: v })} hint="0.9 = 90% yield" />
              <NumField testid="pull-wt" label="Pipe weight" unit="lb/ft" value={pull.pipe_weight_ppf} onChange={(v) => setPull({ ...pull, pipe_weight_ppf: v })} />
              <NumField testid="pull-len" label="Stuck depth" unit="ft" value={pull.length_ft} onChange={(v) => setPull({ ...pull, length_ft: v })} />
              <NumField testid="pull-mw" label="Mud weight" unit="ppg" value={pull.mw} onChange={(v) => setPull({ ...pull, mw: v })} />
            </div>
            <div className="mt-4 flex justify-between">
              <PrimaryButton testid="pull-run" onClick={() => run("safe_pull_limit", pull, setPullOut)}>Calculate</PrimaryButton>
              <SaveButton getPayload={() => pullOut && { category: "Fishing", calculator: "safe_pull_limit", inputs: pull, outputs: pullOut, units: "field", default_name: "Safe Pull Limit" }} />
            </div>
            {pullOut && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="MYS" value={pullOut.mys_lbf?.toLocaleString()} unit="lbf" />
                <ResultRow label="Buoyancy factor" value={pullOut.buoyancy_factor} unit="—" />
                <ResultRow label="Buoyed string weight" value={pullOut.buoyed_string_weight_lbf?.toLocaleString()} unit="lbf" />
                <ResultRow label="Usable pull" value={pullOut.usable_pull_lbf?.toLocaleString()} unit="lbf" />
                <ResultRow label="Max overpull" value={pullOut.max_overpull_lbf?.toLocaleString()} unit="lbf" emphasis />
                {pullOut.warnings?.map((w, i) => (
                  <div key={i} className="mt-2 border-l-2 border-[#ef4444] bg-[#ef4444]/10 px-3 py-2 text-xs mono text-[#fca5a5] flex gap-2 items-start">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {w}
                  </div>
                ))}
              </div>
            )}
          </CalcCard>

          <CalcCard testid="jar-card" title="Jar Impact" formula="E = F·ΔL/12 ; ΔL from Hooke on above-jar pipe">
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="jar-f" label="Jar setting" unit="lbf" value={jar.jar_setting_lbf} onChange={(v) => setJar({ ...jar, jar_setting_lbf: v })} />
              <NumField testid="jar-len" label="Length above jar" unit="ft" value={jar.length_above_jar_ft} onChange={(v) => setJar({ ...jar, length_above_jar_ft: v })} />
              <NumField testid="jar-wt" label="Pipe weight" unit="lb/ft" value={jar.pipe_weight_ppf} onChange={(v) => setJar({ ...jar, pipe_weight_ppf: v })} />
              <NumField testid="jar-od" label="OD" unit="in" value={jar.od} onChange={(v) => setJar({ ...jar, od: v })} />
              <NumField testid="jar-id" label="ID" unit="in" value={jar.id} onChange={(v) => setJar({ ...jar, id: v })} />
            </div>
            <div className="mt-4 flex justify-between">
              <PrimaryButton testid="jar-run" onClick={() => run("jar_impact", jar, setJarOut)}>Calculate</PrimaryButton>
              <SaveButton getPayload={() => jarOut && { category: "Fishing", calculator: "jar_impact", inputs: jar, outputs: jarOut, units: "field", default_name: "Jar Impact" }} />
            </div>
            {jarOut && !jarOut.error && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Above-jar stretch" value={jarOut.above_jar_stretch_in} unit="in" />
                <ResultRow label="Impact energy" value={jarOut.impact_energy_ft_lb?.toLocaleString()} unit="ft·lb" emphasis />
                <ResultRow label="Impact velocity" value={jarOut.impact_velocity_fps} unit="ft/s" />
                <ResultRow label="Peak impact force (~3x)" value={jarOut.peak_impact_force_lbf?.toLocaleString()} unit="lbf" />
                <ResultRow label="String mass above jar" value={jarOut.string_mass_lb?.toLocaleString()} unit="lb" />
                {jarOut.warnings?.map((w, i) => (
                  <div key={i} className="mt-2 border-l-2 border-[#fbbf24] bg-[#f59e0b]/10 px-3 py-2 text-xs mono text-[#fbbf24]">
                    {w}
                  </div>
                ))}
              </div>
            )}
          </CalcCard>
        </div>
      </Section>
    </div>
  );
}
